import React from 'react';
import { MessageSquare, Mail, MessageCircle, Globe } from 'lucide-react';
import { ChannelType, CHANNEL_INFO } from '../../types/conversation';

// Dynamically import lucide icons (Facebook/Instagram not in default export)
const getChannelIcon = (channel: ChannelType) => {
  switch (channel) {
    case 'sms':
      return MessageSquare;
    case 'facebook':
      return MessageCircle; // Using MessageCircle as fallback for Facebook
    case 'instagram':
      return MessageCircle; // Using MessageCircle as fallback for Instagram
    case 'gmail':
      return Mail;
    case 'whatsapp':
      return MessageCircle;
    case 'web_chat':
      return Globe;
    default:
      return MessageSquare;
  }
};

interface ChannelBadgeProps {
  channel: ChannelType;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export default function ChannelBadge({ channel, size = 'sm', showLabel = false }: ChannelBadgeProps) {
  const info = CHANNEL_INFO[channel];
  const Icon = getChannelIcon(channel);

  const sizeClasses = {
    sm: 'w-4 h-4 text-xs',
    md: 'w-5 h-5 text-sm',
    lg: 'w-6 h-6 text-base'
  };

  const paddingClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  // Special gradient handling for Instagram
  const getBackgroundStyle = () => {
    if (channel === 'instagram') {
      return {
        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
      };
    }
    return { backgroundColor: info.bgColor };
  };

  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`${paddingClasses[size]} rounded-full flex items-center justify-center`}
        style={getBackgroundStyle()}
        title={info.label}
      >
        <Icon className={`${sizeClasses[size]} text-white`} />
      </div>
      {showLabel && (
        <span className="text-xs text-gray-400 font-medium">{info.label}</span>
      )}
    </div>
  );
}
